import { useCallback, useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { createUseStyles, useTheme } from 'react-jss';
import { useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import { IaTitleAndDescriptionProposal } from '@app/api';
import { Button } from 'components';
import { IaSuggestionBox } from './IaSuggestionBox';

type ProposalField = keyof IaTitleAndDescriptionProposal;

interface Props {
  field: ProposalField;
  proposals?: (IaTitleAndDescriptionProposal[] | undefined)[];
  loading?: boolean;
  generating?: boolean;
  onRetry: () => void;
}

const MAX_PROPOSALS = 3;

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginTop: -theme.marginBase * 2,
    marginBottom: theme.marginBase * 3,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase,
  },
  proposal: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    display: 'flex',
    gap: theme.marginBase,
    alignItems: 'center',
    justifyContent: 'space-between',

    '& + &': {
      borderTop: `1px solid ${theme.colors.grayBorder}`,
      paddingTop: theme.marginBase,
    },
  },
}));

export const IaProposals = ({
  field,
  proposals,
  loading,
  generating,
  onRetry,
}: Props) => {
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { setFieldValue, setFieldTouched } =
    useFormikContext<Partial<IaTitleAndDescriptionProposal>>();

  const select = useCallback(
    (value: string) => {
      setFieldValue(field, value);
      setFieldTouched(field, true, false);
    },
    [field, setFieldValue, setFieldTouched],
  );

  const [displayed, setDisplayed] = useState(0);
  useEffect(() => {
    setDisplayed(Math.max((proposals?.length ?? 0) - 1, 0));
  }, [proposals?.length]);

  const changeDisplayed = (index: number) => setDisplayed(index);

  const props = proposals?.[displayed];
  const displayedProposals = (props || []).slice(0, MAX_PROPOSALS);

  if (!loading && displayedProposals.length === 0) {
    return null;
  }

  return (
    <IaSuggestionBox
      className={classes.container}
      title={t('editSession.public.iaProposals.title')}
      loading={loading}
      generating={generating}
      loadingLabel={t('editSession.public.iaProposals.loading')}
      displayed={displayed}
      attempts={proposals?.length}
      changeDisplayed={changeDisplayed}
      onRetry={onRetry}
    >
      <div className={classes.list}>
        {displayedProposals.map((proposal, index) => {
          const value = proposal[field];
          return (
            <div key={index} className={classes.proposal}>
              {value}
              <Button
                onClick={() => select(value)}
                xsmall
                color="ink"
                disabled={generating ?? loading}
              >
                {t('editSession.public.iaProposals.use')}
              </Button>
            </div>
          );
        })}
      </div>
    </IaSuggestionBox>
  );
};
